import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useDataCache } from './data-cache-context';

const STORAGE_KEY = 'norgas_active_org';

interface ActiveOrg {
  id: string;
  name: string;
}

interface OrgContextType {
  activeOrgId: string;
  activeOrgName: string;
  setActiveOrg: (id: string, name: string) => void;
  resetToOwnOrg: (ownOrgId: string, ownOrgName: string) => void;
  patchActiveName: (name: string) => void;
  isViewingForeignOrg: boolean;
}

const OrgContext = createContext<OrgContextType | null>(null);

export function OrgProvider({
  children,
  ownOrgId,
  ownOrgName,
}: {
  children: React.ReactNode;
  ownOrgId: string;
  ownOrgName: string;
}) {
  const { clearCache } = useDataCache();

  const [activeOrg, setActiveOrgState] = useState<ActiveOrg>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ActiveOrg;
        if (parsed.id && parsed.name) return parsed;
      }
    } catch {
      // ignore
    }
    return { id: ownOrgId, name: ownOrgName };
  });

  // Keep ownOrg as default when first logged in
  useEffect(() => {
    if (!sessionStorage.getItem(STORAGE_KEY)) {
      setActiveOrgState({ id: ownOrgId, name: ownOrgName });
    }
  }, [ownOrgId, ownOrgName]);

  const setActiveOrg = useCallback(
    (id: string, name: string) => {
      const org = { id, name };
      setActiveOrgState(org);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(org));
      clearCache();
    },
    [clearCache],
  );

  const resetToOwnOrg = useCallback(
    (id: string, name: string) => {
      const org = { id, name };
      setActiveOrgState(org);
      sessionStorage.removeItem(STORAGE_KEY);
      clearCache();
    },
    [clearCache],
  );

  // Update the display name only — no cache clear, used on mount to resolve the name from the API
  const patchActiveName = useCallback((name: string) => {
    setActiveOrgState(prev => {
      const updated = { ...prev, name };
      if (prev.id === ownOrgId) {
        // Don't persist to sessionStorage for own org — it's always re-resolved on mount
      } else {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      }
      return updated;
    });
  }, [ownOrgId]);

  return (
    <OrgContext.Provider
      value={{
        activeOrgId: activeOrg.id,
        activeOrgName: activeOrg.name,
        setActiveOrg,
        resetToOwnOrg,
        patchActiveName,
        isViewingForeignOrg: activeOrg.id !== ownOrgId,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg(): OrgContextType {
  const context = useContext(OrgContext);
  if (!context) throw new Error('useOrg must be used within OrgProvider');
  return context;
}
